import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Camera, Check, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

export default function TeamAccept() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState({ status: 'idle', message: '' });

  const accept = async () => {
    if (!token) return;
    setState({ status: 'pending', message: '' });
    try {
      const res = await api.post('/team/accept', { token });
      await refresh?.();
      setState({ status: 'ok', message: t('team.acceptOk', { role: res.data?.role || '' }) });
      toast.success(t('team.acceptOk', { role: res.data?.role || '' }));
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      const msg = err.response?.data?.detail || t('team.acceptError');
      setState({ status: 'error', message: msg });
    }
  };

  useEffect(() => {
    if (!token) {
      setState({ status: 'error', message: t('team.acceptMissingToken') });
      return;
    }
    if (user) {
      accept();
    }
  }, [user, token]);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="p-6 max-w-md w-full text-center">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="font-semibold text-lg">{t('team.acceptMissingToken')}</h1>
        </Card>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="p-6 max-w-md w-full text-center">
          <Camera className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
          <h1 className="font-semibold text-lg">{t('team.acceptLoginNeeded')}</h1>
          <p className="text-sm text-slate-600 mt-1">{t('team.acceptLoginHint')}</p>
          <div className="flex gap-2 justify-center mt-4">
            <Link to={`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}>
              <Button>{t('auth.signIn')}</Button>
            </Link>
            <Link to={`/register?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}>
              <Button variant="outline">{t('auth.signUp')}</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="p-6 max-w-md w-full text-center">
        {state.status === 'ok' ? (
          <>
            <Check className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
            <h1 className="font-semibold text-lg">{t('team.acceptOkTitle')}</h1>
            <p className="text-sm text-slate-600 mt-1">{state.message}</p>
          </>
        ) : state.status === 'error' ? (
          <>
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <h1 className="font-semibold text-lg">{t('team.acceptErrorTitle')}</h1>
            <p className="text-sm text-slate-600 mt-1">{state.message}</p>
            <Button className="mt-4" onClick={accept}>{t('common.retry')}</Button>
          </>
        ) : (
          <>
            <Camera className="w-10 h-10 text-emerald-600 mx-auto mb-3 animate-pulse" />
            <p className="text-sm text-slate-600">{t('team.acceptInProgress')}</p>
          </>
        )}
      </Card>
    </div>
  );
}
